import { alias, list, map, object, primitive, serializable } from "serializr"
import ValueMod from "@/effects/ValueMod"
import EffectType from "@/effects/EffectType"

export default class Effect {
  @serializable(primitive({
    beforeDeserialize: (callback, jsonValue, jsonParentValue) => {
      if (jsonValue == undefined) callback(null, jsonParentValue.researchId) // Upgrade from v0.6.0
      else callback(null, jsonValue)
    }
  }))
  public readonly source: string
  @serializable(primitive({
    beforeDeserialize: (callback, jsonValue, jsonParentValue) => {
      if (jsonValue == undefined) callback(null, jsonParentValue.effectName) // Upgrade from v0.6.0
      else callback(null, jsonValue)
    }
  }))
  public readonly modName: string
  @serializable(object(ValueMod))
  private targetMod: ValueMod
  @serializable(object(EffectType))
  private type: EffectType
  @serializable
  private _timeLapsed: number = 0
  @serializable
  public isBeingRemoved: boolean = false

  constructor(source: string, modName: string, mod: ValueMod, type: EffectType) {
    this.source = source
    this.modName = modName
    this.targetMod = mod
    this.type = type
  }

  update(dt: number) {
    if (!this.isBeingRemoved || this.type.permanence == 'permanent') {
      this.timeLapsed += dt
    }
    if (this.isBeingRemoved) {
      this.timeLapsed -= dt
    }

    // If a reverting effect has completed, start removing it
    if (this.type.permanence == 'reverting' && this.fractionComplete == 1) {
      this.startRemoving()
    }
  }

  /** Updates the values of this effect to reflect the new one but does not change timeLapsed etc. */
  replace(effect: Effect) {
    this.targetMod = effect.targetMod
    this.type = effect.type
  }

  /**
   * This signals the effect to start removing itself.
   */
  startRemoving() {
    this.isBeingRemoved = true
  }

  stopRemoving() {
    this.isBeingRemoved = false
  }

  get id(): string {
    return this.source + ' - ' + this.modName
  }

  get mod(): ValueMod {
    let modValue = this.targetMod.value

    if (this.type.timeType == 'slow') {
      if (this.targetMod.type == 'add') {
        modValue = modValue * this.fractionComplete
      } else if (this.targetMod.type == 'mul') {
        modValue = 1 + (modValue - 1) * this.fractionComplete
      }
    } else if (this.type.timeType == 'instant' && this.type.duration > 0) {
      if (this.targetMod.type == 'add') {
        modValue = modValue * (1 - this.fractionComplete)
      } else if (this.targetMod.type == 'mul') {
        modValue = 1 + (modValue - 1) * (1 - this.fractionComplete)
      }
    }

    return new ValueMod(modValue, this.targetMod.type)
  }

  get timeLapsed(): number {
    return this._timeLapsed
  }

  set timeLapsed(value: number) {
    this._timeLapsed = Math.max(Math.min(value, this.type.duration), 0)
  }

  get fractionComplete(): number {
    if (this.type.duration == 0) return 1
    return Math.max(Math.min(this._timeLapsed / this.type.duration, 1), 0)
  }

  set fractionComplete(fraction: number) {
    this.timeLapsed = this.type.duration * fraction
  }

  get shouldBeRemoved(): boolean {
    // Instant permanent effects should just never be removed. (Slow permanent handles this via timelapsed.)
    if (this.type.permanence == 'permanent' && this.type.timeType == 'instant') return false

    // Remove if we are being removed and we have decreased time back down to 0.
    if (this.isBeingRemoved && this.timeLapsed == 0) return true

    // Remove temporary instant effects with duration after their duration has run out
    if (this.type.permanence == 'temporary' && this.type.timeType == 'instant' && this.type.duration > 0 && this.fractionComplete == 1) return true

    return false
  }

  modString(): string {
    let s = `${this.modName} ${this.mod.toString()}`
    return s
  }

  equals(e: Effect) {
    return this.source == e.source && this.modName == e.modName && this.targetMod.equals(e.targetMod) && this.type.equals(e.type)
  }
}
